import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as types from '../types';

interface UsePromoCodeEligibilityProps {
  cart: types.Cart;
  showCoupons: boolean;
}

interface PromoCodeEligible {
  canApplyCode: true;
  ineligibilityReason?: undefined;
}

interface PromoCodeIneligible {
  canApplyCode: false;
  ineligibilityReason: string;
}

export type IndividualPromoCodeEligibility = PromoCodeEligible | PromoCodeIneligible;

interface PromoCodeEligibility {
  couponCodeEligibility: IndividualPromoCodeEligibility;
  giftCardEligibility: IndividualPromoCodeEligibility;
  referralCodeEligibility: IndividualPromoCodeEligibility;
}

export default function usePromoCodeEligibility({
  cart,
  showCoupons,
}: UsePromoCodeEligibilityProps): PromoCodeEligibility {
  const { t } = useTranslation();
  const enableGiftCards = useSelector((state: types.AppState) => !!state.config.siteConfig.enableGiftCards);

  const giftCardEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!enableGiftCards) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('gift_cards_cannot_be_redeemed_at_this_time'),
      };
    }

    return { canApplyCode: true };
  }, [enableGiftCards, t]);

  const referralCodeEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!showCoupons) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('a_referral_code_may_not_be_applied_to_this_order'),
      };
    }

    return { canApplyCode: true };
  }, [showCoupons, t]);

  const couponCodeEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!showCoupons) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('a_coupon_may_not_be_applied_to_this_order'),
      };
    }

    return { canApplyCode: true };
  }, [showCoupons, t]);

  return {
    couponCodeEligibility,
    giftCardEligibility,
    referralCodeEligibility,
  };
}
